// mirror of auware/.../frontend/src/components/ui/Spinner.tsx (see frontend/CLAUDE.md mirror policy).
import { forwardRef, type SVGAttributes } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Size presets for the spinner. */
type SpinnerSize = "xs" | "sm" | "md" | "lg";

export interface SpinnerProps extends SVGAttributes<SVGSVGElement> {
  /** Size preset. Defaults to `"md"`. */
  size?: SpinnerSize;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sizeClasses: Record<SpinnerSize, string> = {
  xs: "h-3 w-3",
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Standalone loading spinner using the same SVG animation as Button.tsx.
 *
 * ```tsx
 * <Spinner size="lg" />
 * ```
 */
const Spinner = forwardRef<SVGSVGElement, SpinnerProps>(
  ({ className = "", size = "md", ...props }, ref) => {
    return (
      <svg
        ref={ref}
        className={`animate-spin text-primary-600 ${sizeClasses[size]} ${className}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
        {...props}
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
    );
  },
);

Spinner.displayName = "Spinner";

export default Spinner;
